import { GraphQLResult, GRAPHQL_AUTH_MODE } from '@aws-amplify/api-graphql';
import { API } from 'aws-amplify';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';
import { GetMessagesQuery, MessageListItem } from '../../../API';
import Loading from '../../../components/shared-components/Loading';
import { getMessages } from '../../../graphql/queries';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Filter } from './MessagesFilter';

const MESSAGES_LOAD = 8;

type MessageListProps = {
	filter: Filter;
	refreshData: boolean;
	onMessageClick?: (message: MessageListItem) => void;
};

const MessagesList: React.FC<MessageListProps> = ({ filter, refreshData, onMessageClick }) => {
	const [messages, setMessages] = useState<Array<MessageListItem>>([]);
	const [filteredMessages, setFilteredMessages] = useState<Array<MessageListItem>>([]);
	const [nextToken, setNextToken] = useState<string | null>(null);
	const [hasMoreItem, setHasMoreItem] = useState<boolean>(true);
	const [activeMessage, setActiveMessage] = useState<string>('');

	const applyFilter = () => {
		const filteredArray: Array<MessageListItem> = [];

		if (
			filter &&
			filter.text !== '' &&
			filter.dateStart !== '' &&
			filter.dateEnd !== '' &&
			filter.category !== ''
		) {
			const dtS = moment(filter.dateStart.substring(0, 10));
			const dtE = moment(filter.dateEnd.substring(0, 10) + 'T23:59');
			const ms: Array<MessageListItem> = messages.filter(
				m =>
					m.messageSubject.toLowerCase().indexOf(filter.text.toLowerCase()) >= 0 &&
					moment(m.createdAt).isBetween(dtS, dtE) &&
					m.messageCategory.toLowerCase() === filter.category.toLowerCase()
			);

			filteredArray.push(...ms);
		} else if (filter && filter.text !== '' && filter.dateStart !== '' && filter.dateEnd !== '') {
			const dtS = moment(filter.dateStart.substring(0, 10));
			const dtE = moment(filter.dateEnd.substring(0, 10) + 'T23:59');
			const ms: Array<MessageListItem> = messages.filter(
				m =>
					m.messageSubject.toLowerCase().indexOf(filter.text.toLowerCase()) >= 0 &&
					moment(m.createdAt).isBetween(dtS, dtE)
			);

			filteredArray.push(...ms);
		} else if (filter && filter.text !== '') {
			const ms: Array<MessageListItem> = messages.filter(
				m => m.messageSubject.toLowerCase().indexOf(filter.text.toLowerCase()) >= 0
			);

			filteredArray.push(...ms);
		} else if (filter && filter.dateStart !== '' && filter.dateEnd !== '' && filter.category !== '') {
			const dtS = moment(filter.dateStart.substring(0, 10));
			const dtE = moment(filter.dateEnd.substring(0, 10) + 'T23:59');
			const ms: Array<MessageListItem> = messages.filter(
				m =>
					moment(m.createdAt).isBetween(dtS, dtE) &&
					m.messageCategory.toLowerCase() === filter.category.toLowerCase()
			);

			filteredArray.push(...ms);
		} else if (filter && filter.dateStart !== '' && filter.dateEnd !== '') {
			const dtS = moment(filter.dateStart.substring(0, 10));
			const dtE = moment(filter.dateEnd.substring(0, 10) + 'T23:59');
			const ms: Array<MessageListItem> = messages.filter(m => moment(m.createdAt).isBetween(dtS, dtE));

			filteredArray.push(...ms);
		} else if (filter && filter.category !== '') {
			const ms: Array<MessageListItem> = messages.filter(
				m => m.messageCategory.toLowerCase() === filter.category.toLowerCase()
			);

			filteredArray.push(...ms);
		} else {
			setFilteredMessages(messages);
			return;
		}

		setFilteredMessages(filteredArray);
	};

	const reloadData = async () => {
		const result: GraphQLResult<GetMessagesQuery> = (await API.graphql({
			query: getMessages,
			authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
			variables: { limit: MESSAGES_LOAD, nextToken: null }
		})) as GraphQLResult<GetMessagesQuery>;

		const m = [];
		m.push(...result.data.getMessages.items);
		setMessages(m);
		if (result.data.getMessages.nextToken) {
			setNextToken(result.data.getMessages.nextToken);
			setHasMoreItem(true);
		} else {
			setHasMoreItem(false);
		}
	};

	const loadMessages = async () => {
		const result: GraphQLResult<GetMessagesQuery> = (await API.graphql({
			query: getMessages,
			authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
			variables: { limit: MESSAGES_LOAD, nextToken: nextToken }
		})) as GraphQLResult<GetMessagesQuery>;

		const m = messages;
		m.push(...result.data.getMessages.items);
		setMessages(m);
		if (result.data.getMessages.nextToken) {
			setNextToken(result.data.getMessages.nextToken);
			setHasMoreItem(true);
		} else {
			setHasMoreItem(false);
		}
	};

	useEffect(() => {
		if (messages.length > 0) {
			if (
				filter &&
				(filter.text !== '' ||
					(filter.dateStart !== undefined && filter.dateEnd !== undefined) ||
					filter.category !== undefined)
			) {
				applyFilter();
			} else {
				setFilteredMessages(messages);
			}
		} else {
			//loadMessages();
		}
	}, [messages]);

	useEffect(() => {
		loadMessages();
	}, []);

	useEffect(() => {
		if (filter) {
			setFilteredMessages([]);
			applyFilter();
		} else {
			//setFilteredMessages(messages);
		}
	}, [filter]);

	useEffect(() => {
		if (refreshData || !refreshData) reloadData();
	}, [refreshData]);

	return (
		<div
			className="d-flex f-column message-list mt-2"
			style={{ height: 74 * MESSAGES_LOAD + 'px', overflow: 'auto' }}
			id="scrollableMessagesDiv"
		>
			<InfiniteScroll
				dataLength={filteredMessages.length}
				next={loadMessages}
				hasMore={hasMoreItem}
				loader={<Loading />}
				scrollableTarget="scrollableMessagesDiv"
				endMessage={
					<p style={{ textAlign: 'center' }}>
						<FormattedMessage id="yay.you.have.seen.it.all" />
					</p>
				}
			>
				{filteredMessages.map((m, i) => {
					return (
						<div
							className={`message-list-detail d-flex f-row mt-1 ${(i + 1) % 2 === 0 ? 'odd' : ''} ${
								m.readed ? 'readed' : 'not-readed'
							} ${activeMessage === 'message-' + i ? 'active' : ''}`}
							key={`message-${i}`}
							onClick={() => {
								console.log('message cliccked');
								if (onMessageClick) {
									onMessageClick(m);
									const msg = messages;
									for (let i = 0; i < msg.length; i++) {
										if (msg[i].PK === m.PK && msg[i].SK === m.SK) msg[i].readed = true;
									}
									setActiveMessage(`message-${i}`);
									setMessages(msg);
								}
							}}
						>
							<div className={`message-list-detail-icon ${(i + 1) % 2 === 0 ? '' : 'odd'}`}>
								{m.messageCategory && (
									<img
										src={`images/${m.messageCategory.toLowerCase()}${
											(i + 1) % 2 !== 0 ? '-odd' : ''
										}.png`}
										alt={m.messageCategory}
									/>
								)}
							</div>
							<div className="message-list-detail-container">
								<div className="message-list-detail-category">
									<FormattedMessage id={m.messageCategory} />
								</div>
								<div className="message-list-detail-subject">
									{m.messageSubject.length > 50
										? m.messageSubject.substring(0, 80) + '...'
										: m.messageSubject}
								</div>
								<div className="message-list-detail-datetime">
									<FormattedDate value={m.createdAt} /> | <FormattedTime value={m.createdAt} />
								</div>
							</div>
						</div>
					);
				})}
			</InfiniteScroll>
		</div>
	);
};

export default MessagesList;
